import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";

import AIWorkerControls from "@/components/AIWorkerControls";
import NexusResearchReview from "@/components/NexusResearchReview";

export default function AIWorker() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setStatus(null);
    setQueue(null);

    Promise.all([
      api.get("/ai-worker/status"),
      api.get("/ai-worker/queue", { params: { limit: 25 } }),
    ]).then(([statusResponse, queueResponse]) => {
      if (!active) return;
      setStatus(statusResponse.data);
      setQueue(queueResponse.data);
    }).catch((err) => {
      if (!active) return;
      setError(formatApiErrorDetail(
        err.response?.data?.detail || t("workerLoadFailed", "Unable to load worker data.")
      ));
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, [reload, t]);

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#0B2545]">
            {t("workerTitle", "Panatlantik AI Worker")}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {t("workerSubtitle", "Investment and research workspace")}
          </p>
        </div>
        <button
          type="button"
          disabled={loading}
          onClick={() => setReload((value) => value + 1)}
          className="px-4 py-2 rounded border border-slate-300 disabled:opacity-50"
        >
          {t("workerRefresh", "Refresh")}
        </button>
      </div>

      <AIWorkerControls
        onStatus={setStatus}
        onFinished={() => setReload((value) => value + 1)}
      />

      <NexusResearchReview reload={reload} />

      {loading && <p role="status">{t("workerLoading", "Loading worker data...")}</p>}
      {error && <p role="alert" className="text-red-700">{error}</p>}

      {!loading && !error && status && queue && (
        <>
          <div className="bg-white border border-slate-200 rounded p-5 space-y-2">
            <h2 className="font-semibold">{t("workerStatusTitle", "Worker status")}</h2>
            <p>
              {t("workerStatusLabel", "Status")}:{" "}
              {t(`workerControls.states.${status.status}`, {
                defaultValue: t("workerControls.unknown"),
              })}
            </p>
            <p className="text-sm text-slate-600">
              {t("workerControls.notice")}
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded p-5">
            <h2 className="font-semibold">
              {t("workerQueueTitle", "Research queue")} ({queue.total})
            </h2>
            {queue.items.length === 0 ? (
              <p className="mt-3 text-slate-600">
                {t("workerQueueEmpty", "No research items yet.")}
              </p>
            ) : (
              <ul className="mt-3 divide-y divide-slate-200">
                {queue.items.map((item, index) => (
                  <li key={item.id || index} className="py-3">
                    <p className="font-medium">{item.title || item.id}</p>
                    <p className="text-sm text-slate-600">{t(`workerControls.states.${item.status}`, {
                      defaultValue: t("workerControls.unknown"),
                    })}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}
